import { Injectable, OnDestroy, RendererFactory2 } from '@angular/core'
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http'
import { BehaviorSubject, EMPTY, Observable, of, Subscription, throwError } from 'rxjs'
import { catchError, debounceTime, filter, map, shareReplay, switchMap, take } from 'rxjs/operators'
import { AuthToken, EventService, WindowRef } from '@spartacus/core'
import { MiChannel, MiPlayerConfig, MiVideo } from './moving-image'
import { environment } from '../../../../../environments/environment'
import { VideoEndedEvent, VideoLengthEvent, VideoPauseEvent, VideoPlayEvent } from './video-events'
import { CmsResultData, Image, MiPlayerApi, MovingImage, SmartSearchChannelResult } from '../../components/types'
import { SmartSearchService } from '../cms-search/smart-search.service'
import { SearchResult } from 'smartsearch.js'
import { MbEventListenerService } from '../mb-event-listener/event-listener.service'
import { RequestNewMiPlayerIdEvent } from '../../components/shared/events/request-new-mi-player-id-event'
import { ObjectUtils } from '../../../shared/utils/object-utils'
import { UcService } from '../../enums/uc-service'
import { CmsHttpStatusCode } from '../../enums/cms-http-status-code'
import { UcManagementService } from '../usercentrics-consent-manager/uc-management.service'
import { MiPlayerCreatedEvent } from '../../components/shared/events/mi-player-created-event'
import { MbConstants } from '../../../shared/mb-constants'
import { UcElementBlockedEvent } from '../../components/shared/events/uc-element-blocked-event'

@Injectable({ providedIn: 'root' })
export class MovingImageService implements OnDestroy {

  private token$ = new BehaviorSubject<AuthToken | undefined>(undefined)
  private isScriptLoaded$ = new BehaviorSubject<boolean>(false)
  private videoPlayedInPercent: number = 0
  private activePlayers = new Set<string>()
  private subscription = new Subscription()

  constructor (private http: HttpClient,
               private events: EventService,
               private winRef: WindowRef,
               private renderer: RendererFactory2,
               private smartSearchService: SmartSearchService,
               private eventService: MbEventListenerService,
               private ucManagement: UcManagementService) {
    this.checkAndAddScript()
    this.getToken()
    this.addPlayerListener()
  }

  addPlayerListener (): void {
    this.subscription.add(this.eventService.retrieve()
      .pipe(filter(event => event.event instanceof UcElementBlockedEvent))
      .subscribe(() => {
        this.activePlayers.forEach(playerId => {
          try {
            this.movingImage?.Collection.removePlayerById(playerId)
          } catch (e) {}
        })
        this.activePlayers.clear()
      }))
  }

  get token (): Observable<AuthToken | undefined> {
    if (!this.winRef.isBrowser()) {
      return EMPTY
    }
    if (this.isTokenExpired()) {
      return this.getToken().pipe(
        take(1),
        switchMap(token => {
          this.token$.next(token)
          return this.token$
        })
      )
    }
    return this.token$
  }

  get movingImage (): MovingImage | undefined {
    return this.winRef.isBrowser() ? (this.winRef.nativeWindow as any).VideoPlayer as MovingImage : undefined
  }

  checkAndAddScript (): void {
    if (!this.winRef.document.querySelector<HTMLScriptElement>('#mi-script')) {
      const renderer = this.renderer.createRenderer(this.winRef.document, null)
      const script = renderer.createElement('script')
      script.onload = () => {
        this.isScriptLoaded$.next(true)
      }
      renderer.setAttribute(script, 'id', 'mi-script')
      renderer.setAttribute(script, 'src', environment.movingImage.script)
      renderer.appendChild(this.winRef.document.body, script)
    }
  }

  isTokenExpired (): boolean {
    return this.token$.value ? new Date() > new Date(this.token$.value.expires_at ?? '') : true
  }

  getVideoById (id: string | undefined): Observable<MiVideo | undefined> {
    if (!this.winRef.isBrowser()) {
      return of(undefined)
    }
    if (!id) {
      return of(undefined)
    }
    const params = new HttpParams()
    params.set('include_custom_metadata', 'true')
    params.set('custom_metadata_field', 'subtitle')
    params.set('metadata_set_id', '2')
    params.set('include_keywords', 'true')
    params.set('ignore_publication_state', 'true')
    params.set('include_channel_assignments', 'true')
    params.set('include_owner', 'true')
    const miUrl = `${environment.movingImage.apiBaseUrl}/v1/vms/${environment.movingImage.videoManagerId}/videos/${id}`
    return this.isScriptLoaded$.pipe(
      debounceTime(this.isScriptLoaded$.value ? 0 : 500),
      switchMap(() => this.token),
      take(1),
      switchMap(token => this.http.get<MiVideo>(miUrl, {
        headers: new HttpHeaders().append('Authorization', `Bearer ${token?.access_token}`)
          .append(MbConstants.UC_SERVICE, UcService.MOVING_IMAGE)
          .append(MbConstants.UC_ID, id),
        params: params
      })),
      shareReplay({ bufferSize: 1, refCount: true }),
      catchError(this.handleError))
  }

  createPlayerForVideo (containerId: string, video: MiVideo | undefined): void {
    if (!video || this.ucManagement.isServiceBlocked(UcService.MOVING_IMAGE)) {
      return
    }
    this.checkAndAddScript()
    this.isScriptLoaded$
      .pipe(
        debounceTime(this.isScriptLoaded$.value ? 0 : 500),
        switchMap(() => this.token),
        take(1))
      .subscribe(token => {
          const config = this.getPlayerConfig(environment.movingImage.player, video, token)
          try {
            const player = this.movingImage?.Collection.getPlayerById(containerId)
            if (this.movingImage && player) {
              player.switchVideo(video?.id)
            }
          } catch (e) {
            try {
              this.movingImage?.Collection.addPlayerById(containerId, config)
              this.activePlayers.add(containerId)
            } catch (ex) {
              this.eventService.update(new RequestNewMiPlayerIdEvent(video?.id))
            }
          }
        }
      )
  }

  getPlayerConfig (playerId: string | undefined, video: MiVideo, token: AuthToken | undefined): MiPlayerConfig {
    return {
      success: (playerApi: MiPlayerApi) => {
        this.eventService.update(new MiPlayerCreatedEvent())
        playerApi.registerEventListener('play', () => this.events.dispatch(new VideoPlayEvent(video.title)))
        playerApi.registerEventListener('pause', (event: any) => {
          const durationInPercent = Math.round(event.time / video.length * 100000)
          if (durationInPercent !== 100) {
            this.events.dispatch(new VideoPauseEvent(video.title, durationInPercent))
          }
        })
        playerApi.registerEventListener('ended', () => this.events.dispatch(new VideoEndedEvent(video.title)))
        playerApi.registerEventListener('timeupdate', (event: any) => {
          const playedInPercent = Math.round(event.time / video.length * 100000)
          if (playedInPercent % 10 === 0 && this.videoPlayedInPercent !== playedInPercent) {
            this.videoPlayedInPercent = playedInPercent
            this.events.dispatch(new VideoLengthEvent(`${this.videoPlayedInPercent}%`))
          }
        })
      },
      parameters: {
        configType: `${environment.movingImage.configType}`,
        playerId: `${playerId}`,
        videoId: `${video.id}`,
        apiUrl: `${environment.movingImage.apiUrl}`,
        flashPath: `${environment.movingImage.flashPath}`,
        token: `${token?.access_token}`,
      }
    }
  }

  mapMiTokenToAuthToken (token: any): AuthToken {
    const now = new Date()
    now.setSeconds(now.getSeconds() + token?.expires_in ?? 0)
    return {
      access_token: token?.access_token ?? '',
      token_type: token?.token_type ?? '',
      refresh_token: token?.refresh_token ?? '',
      expires_at: now.toISOString()
    } as AuthToken
  }

  mapVideos (videos: SearchResult[]): MiVideo[] {
    return videos.map(video => {
      const v = video as CmsResultData
      const description = v?.description?.[0] ?? ''
      return {
        id: v?.uid?.[0] ?? '',
        source: v,
        description: description === 'null' ? '' : description,
        title: v?.title?.[0] ?? '',
        length: +(v?.length?.[0] ?? 0),
        channels: (v?.channels as any ?? []).map((channel: string) => ObjectUtils.parseJSON(channel)),
        stills: (v?.stills ?? []).map((still: string) => ObjectUtils.parseJSON(still)),
        deleted: v?.deleted?.[0] === 'true' ?? false,
        published: v?.published?.[0] === 'true' ?? false,
        thumbnail: v?.thumbnail ?? '',
        downloadable: v?.downloadable?.[0] === 'true' ?? false
      }
    })
  }

  mapChannels (channels: SearchResult[]): MiChannel[] {
    return channels.map(channel => {
      const c = channel as SmartSearchChannelResult
      return {
        id: +(c.uid ?? 0),
        name: (c.name?.shift() ?? ''),
        parentId: +(c.parentId?.shift() ?? 0),
        position: +(c.position?.shift() ?? 0)
      }
    })
  }

  getVideos (): Observable<MiVideo[]> {
    return this.smartSearchService.getAllVideos().pipe(map(this.mapVideos))
  }

  getChannels (): Observable<MiChannel[]> {
    return this.smartSearchService.getAllChannels().pipe(map(this.mapChannels))
  }

  createThumbnail (video: MiVideo | undefined): Image {
    return {
      xl: video?.stills?.find(still => still.quality === '1080p')?.url ?? '',
      lg: video?.stills?.find(still => still.quality === '720p')?.url ?? '',
      md: video?.stills?.find(still => still.quality === '480p')?.url ?? '',
      sm: video?.stills?.find(still => still.quality === '360p')?.url ?? '',
      xs: video?.stills?.find(still => still.quality === '288p')?.url ?? '',
      altText: video?.title ?? '',
      title: video?.title ?? ''
    }
  }

  handleError (error: HttpErrorResponse): Observable<never | undefined> {
    if (error.status === CmsHttpStatusCode.BLOCKED_BY_COOKIE_CONSENT) {
      return of(undefined)
    }
    if (error.error instanceof Error) {
      console.error('An error occurred: ', error.error)
    } else {
      console.error(
        `[${error.status}]: ${error.error}`)
    }
    return throwError('Something went wrong! Please try again later.')
  }

  removeBlockedContentReplacerFromDOM (videoId: string, cssClassName: string) {
    this.ucManagement.removeBlockedContentReplacerFromDOM(videoId, cssClassName)
  }

  enableMiAnalytics () {
    this.movingImage?.Collection.acceptAnalytics()
  }

  ngOnDestroy () {
    this.subscription.unsubscribe()
  }

  private getToken (): Observable<AuthToken> {
    if (!this.winRef.isBrowser()) {
      return EMPTY
    }
    return this.http.get(environment.movingImage.tokenUrl)
      .pipe(
        shareReplay(1),
        take(1),
        catchError(this.handleError),
        map(token => this.mapMiTokenToAuthToken(token))
      )
  }
}

